import { Button, HStack } from "@chakra-ui/react"
import { isAnnotationComplete } from "@pathwright/blocks-core/src/blocks/annotatedassessment/block-type"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import { ImageEdit, SubText } from "@pathwright/blocks/src/shared"
import { Content } from "@pathwright/ui/src/components/content/Content"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import get from "lodash/get"
import React, { useEffect } from "react"
import styled from "styled-components"
import QuestionHeader from "../Question/QuestionHeader"
import { QuestionWrapper } from "./ui"

const StyledContentContainer = styled(Content.Container)`
  .BlockImage {
    cursor: ${(p) =>
      p.disableInput ? `not-allowed !important;` : `crosshair !important;`};
  }
`

const AnnotatedAssessmentView = (props) => {
  const { data, onChange } = props

  const { t } = useTranslate()
  const { setBlockFunctions, setUseAutocomplete, getBlurProps } =
    useBlockCompletion()

  const { templateVariables, disableInput } = useBlocksConfig()

  useEffect(() => {
    setUseAutocomplete(true)
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: isAnnotationComplete
    })
  }, [])

  const getAnnotations = (passedData = null) => {
    return get(passedData || data, "answer.selectedAnnotations", [])
  }

  const validateInput = (data) => {
    const annotations = getAnnotations(data)
    if (!annotations.length) return BLOCK_INPUT_STATES.NONE

    return !data.requiredCount || annotations.length >= data.requiredCount
      ? BLOCK_INPUT_STATES.VALID
      : BLOCK_INPUT_STATES.INVALID
  }

  const handleClearAllAnnotations = () => {
    onChange({ answer: { ...data.answer, selectedAnnotations: [] } }, true)
  }

  const handleChangeAnnotation = (annotations) => {
    const prevAnswer = data.answer || { selectedAnnotations: [] }

    if (data.requiredCount && annotations.length > data.requiredCount) return

    const answer = {
      ...prevAnswer,
      selectedAnnotations: annotations
    }
    onChange({ answer }, true)
  }

  const { total, current } = {
    total: data.requiredCount,
    current: getAnnotations(data).length
  }

  return (
    <StyledContentContainer
      className="AnnotatedAssessmentView"
      disableInput={disableInput}
    >
      <QuestionWrapper {...getBlurProps()}>
        <QuestionHeader {...props} />
        {!!data.requiredCount && (
          <HStack
            mb="1rem"
            mt="1rem"
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            css={{
              "& .ContentText.ql-editor": {
                width: "auto"
              }
            }}
          >
            <SubText
              html={`<span>${t(
                `blocks.annot_assess.${
                  current >= total ? "all_added" : "completed"
                }`
              )} (${current}/${total})</span>`}
              templateContext={templateVariables}
            />
            {!!current && !disableInput && (
              <React.Fragment>
                <span>•</span>
                <Button
                  className="reset-button blank"
                  onClick={handleClearAllAnnotations}
                  variant="plain"
                  bg="transparent"
                  p={0}
                  gap={1}
                >
                  <Pathicon icon="arrow-back-circle" />
                  {t("blocks.annot_assess.reset")}
                </Button>
              </React.Fragment>
            )}
          </HStack>
        )}
      </QuestionWrapper>
      <ImageEdit
        image={data.src}
        altText={data.altText}
        caption={data.caption}
        captionEnabled={!!data.caption}
        annotationsEnabled
        annotationsOnly={!disableInput}
        annotations={getAnnotations(data)}
        onChangeAnnotations={handleChangeAnnotation}
        atMaxAnnotations={total && current > total ? true : false}
        readOnly
      />
    </StyledContentContainer>
  )
}

export default AnnotatedAssessmentView
